import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, RadioGroup, styled } from '@mui/material';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Label from '@/components/@IntranetLibrary/Label';
import {
  RadioGroupV2,
  RadioV2,
} from '@/components/@IntranetLibrary/Radio/RadioV2';
import Select from '@/components/@IntranetLibrary/Select';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  ApprovalClearableInput,
  ApprovalOutlineInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import InfoList from '@/components/Approval/@components/InfoList';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  CardTypeOption,
  NEW_CARD_PLEDGE_ALERT,
  NewCardAgreeAlert,
  NewCardAgreeRow,
  NewCardPledgeBox,
  NewCardTextLink,
  NewCardPledgeText,
  RequiredSectionTitle,
  SectionHint,
  VerifiedText,
} from '@/components/Approval/@components/NewCorporateCard';
import { AiFileSelectField } from '@/components/Common/FileUpload';
import CheckIcon from '@/public/icons/approval/check.svg';
import {
  AI_VERIFIER_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_SIZE,
  getApprovalAiDraftSourceUrl,
} from '@/constant/approval';
import UnionPayIcon from '@/public/icons/approval/unionpay.svg';
import VisaIcon from '@/public/icons/approval/visa.svg';
import type { AttachmentUploadResult } from '@/types/approval';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import {
  formatNewCardPhoneNumber,
  NEW_CARD_CREDIT_INFO_URL,
  NEW_CARD_CREDIT_LIMITS,
  NEW_CARD_TYPES,
  NEW_CORPORATE_CARD_TYPE,
  sanitizeNewCardEnglishName,
  type NewCardDocumentKind,
  type NewCardVerifiedFile,
  type NewCorporateCardFormState,
} from '@/utils/approval/newCorporateCard';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const AI_DRAFT_SOURCE_URL = getApprovalAiDraftSourceUrl(
  NEW_CORPORATE_CARD_TYPE,
);

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const SubField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 440px;
  max-width: 100%;
`;

const CreditInfoAlert = styled('p')`
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--dk-danger, #f55060);
`;

const CARD_ICONS: Record<string, ReactNode> = {
  [NEW_CARD_TYPES[0]]: <VisaIcon width={37} height={12} />,
  [NEW_CARD_TYPES[1]]: <UnionPayIcon width={32} height={20} />,
};

const buildFiles = (uploaded: AttachmentUploadResult, file: File) => [
  { fileUuid: uploaded.uuid, mimeType: file.type },
];

const FAIL_MESSAGE: Record<NewCardDocumentKind, string> = {
  idCard:
    '신분증 인식에 실패했어요. 글자가 또렷하게 보이는 사진을 등록해 주세요.',
  bankBook:
    '통장사본 인식에 실패했어요. 글자가 또렷하게 보이는 사진을 등록해 주세요.',
};

type StageResult = { status?: string; message?: string } | null;

const failMessageOf = (kind: NewCardDocumentKind): string =>
  i18next.t(FAIL_MESSAGE[kind]) || FAIL_MESSAGE[kind];

/** ai-draft-source 결과 — 레거시처럼 stage 결과 중 success 가 하나라도 있으면 통과, error 면 그 사유로 실패 */
const parseResultFor =
  (kind: NewCardDocumentKind) =>
  (data: unknown): { result: true } | { error?: string } | null => {
    const d = data as {
      status?: string | null;
      display?: { results?: StageResult[] };
    } | null;
    if (!d || d.status === 'in_progress') return null;
    const results = (d.display?.results ?? []).filter(
      (r): r is NonNullable<StageResult> => !!r && typeof r === 'object',
    );
    if (results.some((r) => r.status === 'success')) return { result: true };
    const failed = results.find((r) => r.status === 'error');
    if (failed) return { error: failed.message || failMessageOf(kind) };
    if (d.status && /fail|error/i.test(d.status)) {
      return { error: failMessageOf(kind) };
    }
    return null;
  };

const PARSE_RESULT: Record<
  NewCardDocumentKind,
  ReturnType<typeof parseResultFor>
> = {
  idCard: parseResultFor('idCard'),
  bankBook: parseResultFor('bankBook'),
};

const REQUEST_BODY: Record<NewCardDocumentKind, Record<string, unknown>> = {
  idCard: { kind: 'idCard' },
  bankBook: { kind: 'bankBook' },
};

const VERIFIED_TEXT: Record<NewCardDocumentKind, string> = {
  idCard: '신분증 확인 완료',
  bankBook: '통장 사본 확인 완료',
};

type VerifierProps = {
  kind: NewCardDocumentKind;
  label: string;
  value: NewCardVerifiedFile | null;
  onChange: (
    patch: (prev: NewCardVerifiedFile | null) => NewCardVerifiedFile | null,
  ) => void;
};

/** 레거시 approval_ai_verifier — 비공개 업로드 후 AI 로 인식 여부만 확인 */
const DocumentVerifier = ({ kind, label, value, onChange }: VerifierProps) => {
  const { t } = useTranslation();
  const handleUploadStart = useCallback(
    (file: File) =>
      onChange(() => ({
        uuid: '',
        fileName: file.name,
        verified: false,
        processing: true,
      })),
    [onChange],
  );
  const handleUploaded = useCallback(
    (uploaded: AttachmentUploadResult) =>
      onChange((prev) => (prev ? { ...prev, uuid: uploaded.uuid } : prev)),
    [onChange],
  );
  const handleResult = useCallback(
    () => onChange((prev) => (prev ? { ...prev, verified: true } : prev)),
    [onChange],
  );
  const handleProcessingChange = useCallback(
    (processing: boolean) =>
      onChange((prev) => (prev ? { ...prev, processing } : prev)),
    [onChange],
  );

  return (
    <SubField id={`new-card-${kind}`}>
      <Label size={14} isRequired={false} title={t(label)} />
      <AiFileSelectField<true>
        fileName={
          value && (value.processing || value.verified) ? value.fileName : ''
        }
        requestUrl={AI_DRAFT_SOURCE_URL}
        getResultUrl={(id) => `${AI_DRAFT_SOURCE_URL}/${id}`}
        buildFiles={buildFiles}
        requestBody={REQUEST_BODY[kind]}
        documentType={NEW_CORPORATE_CARD_TYPE}
        uploadVariant="ai-verifier"
        allowedExtensions={AI_VERIFIER_ALLOWED_EXTENSIONS}
        maxSize={ATTACHMENT_MAX_SIZE}
        parseResult={PARSE_RESULT[kind]}
        onUploadStart={handleUploadStart}
        onUploaded={handleUploaded}
        onResult={handleResult}
        onProcessingChange={handleProcessingChange}
        result={
          value?.verified && (
            <VerifiedText>
              <CheckIcon width={12} height={12} />
              {t(VERIFIED_TEXT[kind])}
            </VerifiedText>
          )
        }
      />
    </SubField>
  );
};

/** 신규입사자 법인카드 신청(204) 본문 — 레거시 appr_edit_forms_new_card */
const NewCorporateCardField = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'newCorporateCard' });

  const update = useCallback(
    (patch: Partial<NewCorporateCardFormState>) => {
      const current = getValues('newCorporateCard');
      if (!current) return;
      setValue(
        'newCorporateCard',
        { ...current, ...patch },
        { shouldDirty: true },
      );
    },
    [getValues, setValue],
  );

  const updateFile = useCallback(
    (key: 'idCard' | 'bankBook') =>
      (
        patch: (prev: NewCardVerifiedFile | null) => NewCardVerifiedFile | null,
      ) => {
        const current = getValues('newCorporateCard');
        if (!current) return;
        update({ [key]: patch(current[key]) });
      },
    [getValues, update],
  );
  const updateIdCard = useCallback(
    (patch: Parameters<ReturnType<typeof updateFile>>[0]) =>
      updateFile('idCard')(patch),
    [updateFile],
  );
  const updateBankBook = useCallback(
    (patch: Parameters<ReturnType<typeof updateFile>>[0]) =>
      updateFile('bankBook')(patch),
    [updateFile],
  );

  if (!state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  const { applicant } = state;

  return (
    <Box>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('신청자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <InfoList
          rows={[
            { label: t('이름'), value: applicant.name },
            { label: t('사번'), value: applicant.employeeNumber },
            { label: t('소속'), value: applicant.department },
            { label: t('이메일'), value: applicant.email },
            { label: t('입사일'), value: applicant.hireDate },
          ]}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldHeader sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <RequiredSectionTitle>{t('필수 자료 첨부')}</RequiredSectionTitle>
          <SectionHint>
            {t(
              '첨부한 파일은 인식 가능 여부를 확인하며, 법인카드 담당자만 인증 후 확인할 수 있습니다.',
            )}
          </SectionHint>
        </FieldHeader>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <DocumentVerifier
            kind="idCard"
            label="신분증 첨부"
            value={state.idCard}
            onChange={updateIdCard}
          />
          <DocumentVerifier
            kind="bankBook"
            label="급여 통장사본 첨부"
            value={state.bankBook}
            onChange={updateBankBook}
          />
        </Box>
      </FieldBlock>

      <FieldBlock id="new-card-credit-info">
        <RequiredSectionTitle>
          <NewCardTextLink
            href={NEW_CARD_CREDIT_INFO_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('신용 정보 조회 동의서')}
          </NewCardTextLink>
          {t('를 제출하셨습니까?')}
        </RequiredSectionTitle>
        <CreditInfoAlert>
          {t(
            "'신용 정보 조회 동의서' 클릭 시 신용정보 조회 동의 링크로 연결됩니다.",
          )}
        </CreditInfoAlert>
        <Box
          component="label"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            mt: 1,
            cursor: 'pointer',
          }}
        >
          <Checkbox
            size="small"
            checked={state.creditInfoConsent}
            onChange={(e) => update({ creditInfoConsent: e.target.checked })}
          />
          {t('제출 완료')}
        </Box>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <RequiredSectionTitle>{t('영문 이름')}</RequiredSectionTitle>
          <SectionHint>{t('예시) GILDONG HONG')}</SectionHint>
        </FieldHeader>
        <InputColumn>
          <ApprovalOutlineInput
            id="new-card-en-name"
            value={state.enName}
            placeholder={t('영문 이름을 입력해주세요')}
            onChange={(e) =>
              update({ enName: sanitizeNewCardEnglishName(e.target.value) })
            }
          />
          <ApprovalOutlineInput
            id="new-card-en-lastname"
            value={state.enLastname}
            placeholder={t('영문 성을 입력해주세요')}
            onChange={(e) =>
              update({
                enLastname: sanitizeNewCardEnglishName(e.target.value),
              })
            }
          />
        </InputColumn>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <RequiredSectionTitle>{t('SMS 수신 번호')}</RequiredSectionTitle>
          <SectionHint>{t('숫자만 입력해 주세요')}</SectionHint>
        </FieldHeader>
        <InputColumn>
          <ApprovalClearableInput
            id="new-card-phone"
            value={state.phoneNumber}
            placeholder="010-0000-0000"
            inputProps={{ maxLength: 13, inputMode: 'numeric' }}
            onChange={(next) =>
              update({ phoneNumber: formatNewCardPhoneNumber(next) })
            }
          />
        </InputColumn>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <RequiredSectionTitle>{t('이용한도')}</RequiredSectionTitle>
        </FieldHeader>
        <InputColumn>
          <Select
            value={state.creditLimit}
            options={NEW_CARD_CREDIT_LIMITS.map((option) => ({
              value: option.value,
              label: t(option.label),
            }))}
            onChange={(e) => update({ creditLimit: e.target.value as string })}
            sx={approvalOutlineSelectSx}
          />
        </InputColumn>
      </FieldBlock>

      <FieldBlock id="new-card-type">
        <FieldHeader>
          <RequiredSectionTitle>{t('카드 종류')}</RequiredSectionTitle>
        </FieldHeader>
        <RadioGroup
          row
          value={state.cardType}
          onChange={(e) => update({ cardType: e.target.value })}
          sx={{ gap: '8px' }}
        >
          {NEW_CARD_TYPES.map((cardType) => (
            <CardTypeOption
              key={cardType}
              value={cardType}
              control={<RadioV2 />}
              label={
                <>
                  {t(cardType)}
                  {CARD_ICONS[cardType]}
                </>
              }
            />
          ))}
        </RadioGroup>
      </FieldBlock>

      <FieldBlock id="new-card-pledge">
        <FieldHeader>
          <InfoSectionTitle>{t('개인형 법인카드 서약')}</InfoSectionTitle>
        </FieldHeader>
        <NewCardPledgeBox>
          <NewCardPledgeText />
        </NewCardPledgeBox>
        <NewCardAgreeRow>
          <RadioGroupV2
            row
            value={state.pledgeAgree}
            onChange={(e) =>
              update({
                pledgeAgree: e.target
                  .value as NewCorporateCardFormState['pledgeAgree'],
              })
            }
            options={[
              { value: 'Y', label: t('동의함') },
              { value: 'N', label: t('동의하지 않음') },
            ]}
          />
          <NewCardAgreeAlert
            sx={{
              visibility: state.pledgeAgree === 'N' ? 'visible' : 'hidden',
            }}
          >
            {t(NEW_CARD_PLEDGE_ALERT)}
          </NewCardAgreeAlert>
        </NewCardAgreeRow>
      </FieldBlock>
    </Box>
  );
};

export default NewCorporateCardField;
